import { useRef } from 'react';

interface UseHoldProps {
  // the function to call on hold
  onHold: () => void;
  // the interval in milliseconds to call onHold
  ms: number;
}

/**
 * Custom hook to handle mouse hold events. Can be used on a button, or any other element.
 *
 * example:
 * const holdHandlers = useHold({ onHold: () => console.log('holding'), ms: 100 });
 *
 * <button
 *   onMouseDown={holdHandlers.onMouseDown}
 *   onMouseUp={holdHandlers.onMouseUp}
 *   onMouseLeave={holdHandlers.onMouseLeave}
 * >
 *   Hold me
 * </button>
 */

export function useHold({ onHold, ms }: UseHoldProps) {
  const timer = useRef<number>(null);

  const handleOnMouseDown = (event: React.MouseEvent<Element>) => {
    // call onHold immediately. This is so the action happens immediately when the user clicks
    onHold();

    // set up a timer to call onHold every ms milliseconds
    if (timer.current === null) {
      timer.current = window.setInterval(() => {
        onHold();
      }, ms);
    }
  };

  // clear the timer. This is called when the mouse is released or leaves the component
  const cleanup = () => {
    window.clearInterval(timer.current);
    timer.current = null;
  };

  // clear the timer when the mouse is released
  const handleOnMouseUp = () => {
    cleanup();
  };

  // clear the timer when the mouse leaves the component
  const handleOnMouseLeave = () => {
    cleanup();
  };

  return {
    handleOnMouseDown: handleOnMouseDown,
    handleOnMouseUp: handleOnMouseUp,
    handleOnMouseLeave: handleOnMouseLeave,
  };
}
